import { defineComponent as _defineComponent } from 'vue'
import TagInputManager from '@sh/components/MediaManager/TagsContainer/TagInputManager.ts.vue';
import TagsContainer from '@sh/components/MediaManager/TagsContainer/TagsContainer.ts.vue';
import OptimizerIcon from '@sh/components/Utils/OptimizerIcon.ts.vue';
import { MediaManager } from '@sh/types';
import { union } from 'lodash';
import { computed, ref } from 'vue';

type SelectedContentsProps = Array<Record<string, unknown> & { tags: Array<string> }>;

interface Props {
  mode: MediaManager.Mode;
  autocompleteItems: MediaManager.Tag[];
  title?: NullableType<string>;
  isLoading?: boolean;
}

interface Emits {
  (e: 'onAdModalOpen'): void;
  (e: 'handleOnRemoveTags', value: { params: MediaManager.BulkTagsParams; mode: MediaManager.Mode }): void;
  (e: 'onBulkTagInsert', value: { tags: Array<string>; deletedTags: Array<string> }): void;
  (e: 'onTagsSave', value: MediaManager.BulkTagSubmit): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BulkTagInsertModal.ts',
  props: {
    mode: null,
    autocompleteItems: null,
    title: { default: null },
    isLoading: { type: Boolean, default: false }
  },
  emits: ["onAdModalOpen", "handleOnRemoveTags", "onBulkTagInsert", "onTagsSave"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (e: 'onAdModalOpen'): void;
  (e: 'handleOnRemoveTags', value: { params: MediaManager.BulkTagsParams; mode: MediaManager.Mode }): void;
  (e: 'onBulkTagInsert', value: { tags: Array<string>; deletedTags: Array<string> }): void;
  (e: 'onTagsSave', value: MediaManager.BulkTagSubmit): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { mode: MediaManager.Mode, autocompleteItems: MediaManager.Tag[], title: NullableType<string>, isLoading: boolean };





const currentUsedTags = ref<string[]>([]);
const newSelectedTags = ref<string[]>([]);
const isBulkTagInsertModal = ref<boolean>(false);
const selectedContents = ref<SelectedContentsProps>([]);

const replaceMode = computed(() => {
  const mode = props.mode.charAt(0).toUpperCase() + props.mode.slice(1);
  return props.mode === MediaManager.Mode.ADS ? 'Creative' : mode;
});

function getUniqueTags(selectedTags: Array<string> | SelectedContentsProps): string[] {
  return union(
    ...selectedTags.map((content) => {
      if (typeof content === 'string') {
        return [content];
      }
      if ('tags' in content) {
        return content.tags;
      }
      return [];
    })
  );
}

const deletedTags = computed(() => {
  const tags = getUniqueTags(selectedContents.value);
  return tags.filter((tag) => !currentUsedTags.value.includes(tag));
});

function onAddNewTag(data: string): void {
  const newUniqueTags = newSelectedTags.value.includes(data);
  if (!newUniqueTags) {
    newSelectedTags.value.push(data);
  }
}

function onSelectTags(data: SelectOption<string, 'id', 'label'>): void {
  const newUniqueTags = newSelectedTags.value.includes(data.label);
  if (!newUniqueTags) {
    newSelectedTags.value.push(data.label);
  }
}

function onDeselectTags(data: SelectOption<string, 'id', 'label'>): void {
  const index = newSelectedTags.value.indexOf(data.label);
  newSelectedTags.value.splice(index, 1);
}

function getParams(type: 'DELETE' | 'ADD') {
  return {
    id: selectedContents.value.map((content) => content.id),
    tags: type === 'ADD' ? newSelectedTags.value : deletedTags.value,
    type,
  } as MediaManager.BulkTagsParams;
}

function handleOnOpenModal(data: SelectedContentsProps) {
  selectedContents.value = data;
  currentUsedTags.value = getUniqueTags(data);

  isBulkTagInsertModal.value = true;
}

function onTagActionClick(data: SelectedContentsProps) {
  if (data.length === 1) {
    selectedContents.value = data;
    emits('onAdModalOpen');
  } else {
    handleOnOpenModal(data);
  }
}

async function onRemoveTags() {
  emits('handleOnRemoveTags', { params: getParams('DELETE'), mode: props.mode });
}

function onCancelClick(areSavedChanges = false) {
  emits('onBulkTagInsert', { tags: areSavedChanges ? newSelectedTags.value : [], deletedTags: deletedTags.value });
  newSelectedTags.value = [];
  currentUsedTags.value = [];
  selectedContents.value = [];
  isBulkTagInsertModal.value = false;
}

async function onSaveClick() {
  emits('onTagsSave', {
    mode: props.mode,
    addedTags: getParams('ADD'),
    deletedTags: getParams('DELETE'),
  });
  onCancelClick(true);
}

expose({
  handleOnOpenModal,
  onTagActionClick,
});

return { __sfc: true,emits, props, currentUsedTags, newSelectedTags, isBulkTagInsertModal, selectedContents, replaceMode, getUniqueTags, deletedTags, onAddNewTag, onSelectTags, onDeselectTags, getParams, handleOnOpenModal, onTagActionClick, onRemoveTags, onCancelClick, onSaveClick, TagInputManager, TagsContainer, OptimizerIcon }
}

})